// src/components/layout/Navbar/ProjectsDropdown.js
import React, { useEffect, useState } from 'react';
import useOpenAIFetch from 'hooks/useOpenAIFetch';
import { useProjects } from 'contexts/ProjectsContext';
import { useUser } from 'contexts/UserContext';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LuFolder } from 'react-icons/lu';

export default function ProjectsDropdown() {
  const { t } = useTranslation();
  const { fetchedProjects, fetchOpenAIProjects } = useOpenAIFetch();
  const { selectedProjectId, setSelectedProjectId } = useProjects();
  const { user } = useUser();
  
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  
  // Determinar si el usuario puede cambiar de proyecto
  const isRestricted = user && (user.role === 2 || user.role === 3);
  
  // Verificar si el usuario tiene un proyecto asignado
  const hasAssignedProject = isRestricted && user.project_id;

  // Cargar los proyectos disponibles
  useEffect(() => {
    fetchOpenAIProjects();
  }, [fetchOpenAIProjects]);

  // Procesar los proyectos según el rol del usuario
  useEffect(() => {
    if (fetchedProjects.data && fetchedProjects.data.data) {
      const allProjects = fetchedProjects.data.data;
      setProjects(allProjects);
      
      if (isRestricted && hasAssignedProject) {
        // Filtrar para solo mostrar el proyecto asignado
        const userProject = allProjects.find(p => 
          p.id === user.project_id || 
          p.project_id === user.project_id
        );
        
        setFilteredProjects(userProject ? [userProject] : []);
        
        // Forzar la selección del proyecto asignado
        if (userProject && selectedProjectId !== user.project_id) {
          setSelectedProjectId(user.project_id);
        }
      } else {
        // Para administradores o usuarios sin restricciones
        setFilteredProjects(allProjects);
      }
    }
  }, [fetchedProjects.data, user, selectedProjectId, setSelectedProjectId, isRestricted, hasAssignedProject]);

  // Los clientes (roles 2 y 3) tienen un único proyecto fijo: no hay nada que
  // elegir, así que el selector solo se muestra a los administradores.
  if (isRestricted) return null;

  return (
    <label className="flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white pl-2.5 pr-1 text-sm text-gray-700 focus-within:border-brand focus-within:ring-4 focus-within:ring-brand-soft">
      <LuFolder size={16} className="flex-none text-gray-500" aria-hidden="true" />
      <span className="sr-only">{t('layout:navbar.project')}</span>
      <select
        value={selectedProjectId}
        onChange={(e) => {
          const newProjectId = e.target.value;
          setSelectedProjectId(newProjectId);
        }}
        className="h-full max-w-[200px] cursor-pointer truncate bg-transparent pr-1 font-medium text-gray-900 focus:outline-none"
      >
        <option value="">{t('projects_dropdown.placeholder')}</option>
        {filteredProjects.map((project) => (
          <option key={project.id} value={project.id}>
            {project.name}
          </option>
        ))}
      </select>
    </label>
  );
}

// validación de props
ProjectsDropdown.propTypes = {
  selectedProjectId: PropTypes.string,
  setSelectedProjectId: PropTypes.func,
};